import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Upload, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { RichTextEditor } from '../RichTextEditor';
import { getQuoteDescriptionSuggestions, getQuoteDescriptionById } from '../../services/api';
import { formatDescriptionHtml } from './QuoteHelpers';

export function DescriptionModal({ isOpen, onClose, onSubmit, loading, editData }) {
  const initDesc = (() => {
    if (!editData) return '';
    let d = editData.description || '';
    if (d && !/<(p|ol|ul|li|h[1-6]|div|br|blockquote)\b/i.test(d)) {
      d = d.split('\n').filter(Boolean).map(line => `<p>${line}</p>`).join('');
    } else if (d) {
      // Clean up broken contentEditable output in the editor too
      d = formatDescriptionHtml(d);
    }
    return d;
  })();
  const [formData, setFormData] = useState({ job_title: editData?.job_title || '', description: initDesc });
  const [attachments, setAttachments] = useState(editData?.attachments || []);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const fileInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleJobTitleChange = (e) => {
    const val = e.target.value;
    setFormData({ ...formData, job_title: val });

    // Debounced search for suggestions
    if (searchTimeout) clearTimeout(searchTimeout);
    if (val.length >= 1) {
      const timeout = setTimeout(() => {
        getQuoteDescriptionSuggestions(val).then(res => {
          setSuggestions(res.data || []);
          setShowSuggestions((res.data || []).length > 0);
        }).catch(() => {});
      }, 300);
      setSearchTimeout(timeout);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    // Fetch full description data and auto-populate
    getQuoteDescriptionById(suggestion.id).then(res => {
      setFormData({
        job_title: res.data.job_title || suggestion.job_title,
        description: res.data.description || ''
      });
    }).catch(() => {
      setFormData({ ...formData, job_title: suggestion.job_title });
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAttachments(prev => [...prev, { name: file.name, data: ev.target.result, type: file.type }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAttachments(prev => [...prev, { name: file.name, data: ev.target.result, type: file.type }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.job_title) { toast.error('Please enter job title'); return; }
    onSubmit({ ...formData, attachments });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="font-semibold">{editData ? 'Edit Description' : 'Add new line'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="relative" ref={suggestionsRef}>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Job Title</label>
            <input
              type="text"
              value={formData.job_title}
              onChange={handleJobTitleChange}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary"
              placeholder="e.g., ROOF WORKS, INTERIOR PAINTING"
              autoComplete="off"
              data-testid="description-job-title"
            />
            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="job-title-suggestions">
                {suggestions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => handleSelectSuggestion(s)}
                    className="px-4 py-2.5 hover:bg-slate-100 cursor-pointer flex justify-between items-center text-sm border-b last:border-b-0"
                    data-testid={`suggestion-${s.id}`}
                  >
                    <span className="text-slate-800">{s.job_title}</span>
                    <span className="text-slate-400 text-xs ml-2">{s.quote_number}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase">Description</label>
            <RichTextEditor 
              value={formData.description} 
              onChange={(val) => setFormData({ ...formData, description: val })}
              placeholder="Enter detailed description..."
            />
          </div>

          {/* Attachments Section */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Attachments</label>
            <div 
              className="border-2 border-dashed border-slate-300 rounded p-6 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-500">Drop your attachments here.</p>
              <input type="file" ref={fileInputRef} className="hidden" multiple onChange={handleFileChange} />
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {attachments.map((file, idx) => (
                  <div key={file.name + idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <Paperclip size={16} className="text-slate-400" />
                      <span className="text-sm truncate">{file.name}</span>
                    </div>
                    <button type="button" onClick={() => removeAttachment(idx)} className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded flex items-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {editData ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Email Quote Modal
